import { useCallback, useMemo, useState } from 'react'
import { sortDataTableRows } from '../DataTable.helpers'
import { DataTableColumn, DataTableSortKey } from '../DataTable.types'
import { DataTableSort, useDataTableSort } from './useDataTableSort'
import { DataTableRowsPerPage, DEFAULT_ROWS_PER_PAGE, useDataTableRowsPerPage } from './useDataTableRowsPerPage'

type UseDataTableArgs<TRow, TColumnId extends string> = {
    rows: TRow[]
    columns: DataTableColumn<TRow, TColumnId>[]
    pinnedSort?: DataTableSortKey<TColumnId>
    defaultSortColumnId?: TColumnId
    sort?: DataTableSort<TColumnId>
    rowsPerPageState?: DataTableRowsPerPage
    hiddenColumnIds?: readonly string[]
    isSortClearable?: boolean
}

export const useDataTable = <TRow, TColumnId extends string>({
    rows,
    columns,
    sort,
    rowsPerPageState,
    pinnedSort,
    defaultSortColumnId,
    hiddenColumnIds,
    isSortClearable,
}: UseDataTableArgs<TRow, TColumnId>) => {
    const ownSort = useDataTableSort<TColumnId>(defaultSortColumnId, isSortClearable)
    const ownRowsPerPage = useDataTableRowsPerPage()

    const { sortKey: requestedSortKey, handleSort: applySort } = sort ?? ownSort
    const { rowsPerPage, setRowsPerPage } = rowsPerPageState ?? ownRowsPerPage

    const sortKey = requestedSortKey && !hiddenColumnIds?.includes(requestedSortKey.columnId) ? requestedSortKey : null

    const sortState = useMemo(
        () => [pinnedSort, sortKey].filter((key): key is DataTableSortKey<TColumnId> => !!key),
        [pinnedSort, sortKey]
    )

    const [requestedPage, setRequestedPage] = useState(0)

    const handlePageChange = useCallback((nextPage: number) => {
        setRequestedPage(nextPage)
    }, [])

    const handleRowsPerPageChange = useCallback(
        (nextRowsPerPage: number) => {
            setRowsPerPage(nextRowsPerPage)
            setRequestedPage(0)
        },
        [setRowsPerPage]
    )

    const handleSort = useCallback(
        (columnId: TColumnId) => {
            applySort(columnId)
            setRequestedPage(0)
        },
        [applySort]
    )

    const sortedRows = useMemo(() => sortDataTableRows(rows, columns, sortState), [rows, columns, sortState])

    const lastPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1)
    const page = Math.min(requestedPage, lastPage)

    const paginatedRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    )

    return {
        sortKey,
        handleSort,
        isInStoredOrder: !sortState.length,

        page,
        rowsPerPage,
        handlePageChange,
        handleRowsPerPageChange,
        isPaginationVisible: sortedRows.length > DEFAULT_ROWS_PER_PAGE,

        sortedRows,
        paginatedRows,
    }
}
